import React, { useState, useEffect } from 'react';
import { ArrowLeft, Wallet, CheckCircle, Copy, AlertTriangle, Info, DollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import { APP_CONFIG } from '../../../config/app-config';
import { getToken as getSessionToken } from '../../../utils/authToken';
import { BrandSpinner } from '../../common/LoadingSpinner';
import { formatPinForDisplay, pinToClipboardValue } from '../../../utils/formatPin';

interface TopupRequestData {
  original_amount: number;
  issued_to: string;
  description?: string;
}

interface TopupQuote {
  walletCredit: number;
  fee: number;
  tender: number;
  minWalletCredit: number;
  maxWalletCredit: number;
}

type Step = 'form' | 'processing' | 'success' | 'error';

export function TopupEasyPayOverlay() {
  const navigate = useNavigate();
  
  // Form state
  const [amount, setAmount] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<Step>('form');
  const [quote, setQuote] = useState<TopupQuote | null>(null);
  const [limits, setLimits] = useState<{ minWalletCredit: number; maxWalletCredit: number } | null>(null);
  const [errors, setErrors] = useState<{ amount?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isQuoting, setIsQuoting] = useState(false);
  
  // Success state
  const [easyPayPIN, setEasyPayPIN] = useState<string>('');
  const [issuedQuote, setIssuedQuote] = useState<TopupQuote | null>(null);
  const [copiedPIN, setCopiedPIN] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Quick amount options
  const quickAmounts = [50, 100, 200, 300, 500, 1000, 2000];

  const formatRand = (value: number) => value.toLocaleString('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const minWallet = limits?.minWalletCredit ?? 50;
  const maxWallet = limits?.maxWalletCredit;

  const validateAmount = (value: string): boolean => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < minWallet) return false;
    if (maxWallet != null && numValue > maxWallet + 0.001) return false;
    return numValue <= 4000;
  };

  useEffect(() => {
    let cancelled = false;
    const loadLimits = async () => {
      try {
        const token = getSessionToken();
        const response = await fetch(`${APP_CONFIG.API.baseUrl}/api/v1/vouchers/easypay/topup-quote`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) return;
        const body = await response.json();
        if (!cancelled && body?.data?.minWalletCredit != null && body?.data?.maxWalletCredit != null) {
          setLimits({
            minWalletCredit: Number(body.data.minWalletCredit),
            maxWalletCredit: Number(body.data.maxWalletCredit),
          });
        }
      } catch {
        // The amount quote request surfaces the fee error.
      }
    };
    loadLimits();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!amount || !validateAmount(amount)) {
      setQuote(null);
      setIsQuoting(false);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setIsQuoting(true);
      try {
        const token = getSessionToken();
        const response = await fetch(
          `${APP_CONFIG.API.baseUrl}/api/v1/vouchers/easypay/topup-quote?amount=${encodeURIComponent(amount)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const body = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (!response.ok) {
          setQuote(null);
          setErrors({ amount: body.error || 'EasyPay fee could not be quoted' });
          return;
        }
        setErrors({});
        setQuote({
          walletCredit: Number(body.data.walletCredit),
          fee: Number(body.data.fee),
          tender: Number(body.data.tender),
          minWalletCredit: Number(body.data.minWalletCredit),
          maxWalletCredit: Number(body.data.maxWalletCredit),
        });
      } catch {
        if (!cancelled) {
          setQuote(null);
          setErrors({ amount: 'EasyPay fee could not be quoted' });
        }
      } finally {
        if (!cancelled) setIsQuoting(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [amount, minWallet, maxWallet]);

  // Handle amount input
  const handleAmountChange = (value: string) => {
    // Allow only numbers and decimal point
    const cleanValue = value.replace(/[^0-9.]/g, '');
    setAmount(cleanValue);
    
    // Clear amount error if valid
    if (validateAmount(cleanValue)) {
      setErrors(prev => ({ ...prev, amount: undefined }));
    }
  };

  // Handle quick amount selection
  const handleQuickAmount = (quickAmount: number) => {
    setAmount(quickAmount.toString());
    setErrors(prev => ({ ...prev, amount: undefined }));
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: { amount?: string } = {};
    
    if (!amount || !validateAmount(amount)) {
      newErrors.amount = maxWallet != null
        ? `Amount must be between R${formatRand(minWallet)} and R${formatRand(maxWallet)}`
        : `Amount must be at least R${formatRand(minWallet)}`;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setCurrentStep('processing');
    
    try {
      const token = getSessionToken();
      const requestData: TopupRequestData = {
        original_amount: parseFloat(amount),
        issued_to: 'self',
        description: 'Top-up at EasyPay'
      };
      
      const response = await fetch(`${APP_CONFIG.API.baseUrl}/api/v1/vouchers/easypay/issue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to create top-up request';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || `Server returned ${response.status}`;
        }
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      
      // Set success data
      setEasyPayPIN(result.data.easypay_code || '');
      setIssuedQuote({
        walletCredit: Number(result.data.amount),
        fee: Number(result.data.fee),
        tender: Number(result.data.tender_amount),
        minWalletCredit: quote?.minWalletCredit ?? minWallet,
        maxWalletCredit: quote?.maxWalletCredit ?? maxWallet ?? minWallet,
      });
      setCurrentStep('success');
      
    } catch (error: any) {
      console.error('Top-up request error:', error);
      setErrorMessage(error?.message || 'We couldn\'t create your top-up request. Please try again.');
      setCurrentStep('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Copy PIN to clipboard — raw digits, no spaces (display is grouped, copy is not).
  const handleCopyPIN = async () => {
    try {
      await navigator.clipboard.writeText(pinToClipboardValue(easyPayPIN));
      setCopiedPIN(true);
      setTimeout(() => setCopiedPIN(false), 2000);
    } catch (err) {
      console.error('Failed to copy PIN:', err);
    }
  };

  // Reset form
  const handleReset = () => {
    setAmount('');
    setQuote(null);
    setIssuedQuote(null);
    setErrors({});
    setEasyPayPIN('');
    setCopiedPIN(false);
    setCurrentStep('form');
  };

  return (
    <div style={{ 
      backgroundColor: '#ffffff',
      minHeight: '100vh',
      fontFamily: 'Montserrat, sans-serif',
      padding: 'var(--mobile-padding)'
    }}>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => currentStep === 'form' ? navigate(-1) : handleReset()}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div className="flex-1">
          <h1 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
            fontWeight: 'var(--font-weight-bold)',
            color: '#1f2937'
          }}>
            Top-up at EasyPay
          </h1>
          <p style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 'var(--mobile-font-small)',
            fontWeight: 'var(--font-weight-normal)',
            color: '#6b7280',
            marginTop: '4px'
          }}>
            {currentStep === 'form' ? 'Create top-up request' : 
             currentStep === 'processing' ? 'Processing request...' :
             currentStep === 'success' ? 'Top-up request created' : 'Request failed'}
          </p>
        </div>
      </div>

      {/* Form Step */}
      {currentStep === 'form' && (
        <div className="space-y-6">
          {/* Info Alert */}
          <Alert className="bg-blue-50 border-blue-200">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: 'var(--mobile-font-small)',
              color: '#1e40af'
            }}>
              <strong>How it works:</strong> Choose the amount to add to your wallet. The EasyPay fee is added at the till. Your wallet is credited the amount you choose after you pay.
            </AlertDescription>
          </Alert>

          {/* Amount Input Card */}
          <Card>
            <CardHeader>
              <CardTitle style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: 'var(--mobile-font-base)',
                fontWeight: 'var(--font-weight-bold)',
                color: '#1f2937'
              }}>
                Top-up Amount
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Amount Input */}
              <div>
                <Label style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: 'var(--mobile-font-small)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: '#374151'
                }}>
                  {maxWallet != null
                    ? `Wallet credit (R${formatRand(minWallet)} - R${formatRand(maxWallet)})`
                    : 'Wallet credit'}
                </Label>
                <div className="relative mt-2">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                    R
                  </span>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    placeholder="0.00"
                    className={`pl-8 pr-4 ${errors.amount ? 'border-red-500' : ''}`}
                    style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: 'var(--mobile-font-base)',
                      fontWeight: 'var(--font-weight-medium)',
                      height: '48px'
                    }}
                  />
                </div>
                {errors.amount && (
                  <p className="text-red-500 text-sm mt-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {errors.amount}
                  </p>
                )}
              </div>

              {/* Quick Amount Buttons */}
              <div>
                <Label style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: 'var(--mobile-font-small)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: '#374151'
                }}>
                  Quick Select
                </Label>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {quickAmounts.map((qa) => (
                    <button
                      key={qa}
                      onClick={() => handleQuickAmount(qa)}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-[#86BE41] hover:text-white hover:border-[#86BE41] transition-colors"
                      style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: 'var(--mobile-font-small)',
                        fontWeight: 'var(--font-weight-medium)',
                        backgroundColor: amount === qa.toString() ? '#86BE41' : 'white',
                        color: amount === qa.toString() ? 'white' : '#374151'
                      }}
                    >
                      R{qa}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {quote && (
            <Card>
              <CardContent className="space-y-2 p-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Wallet credit</span>
                  <span className="font-semibold">R{formatRand(quote.walletCredit)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">EasyPay fee</span>
                  <span className="font-semibold">R{formatRand(quote.fee)}</span>
                </div>
                <div className="flex items-center justify-between border-t border-gray-100 pt-2 text-base">
                  <span className="font-semibold text-gray-900">Pay at EasyPay</span>
                  <span className="font-bold text-gray-900">R{formatRand(quote.tender)}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={!quote || isQuoting || isSubmitting}
            className="w-full bg-gradient-to-r from-[#86BE41] to-[#2D8CCA] text-white border-none"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: 'var(--mobile-font-base)',
              fontWeight: 'var(--font-weight-medium)',
              padding: '16px',
              minHeight: 'var(--mobile-touch-target)',
              opacity: !quote || isQuoting || isSubmitting ? 0.6 : 1
            }}
          >
            {isSubmitting ? 'Creating Request...' : 'Create Top-up Request'}
          </Button>
        </div>
      )}

      {/* Processing Step */}
      {currentStep === 'processing' && (
        <BrandSpinner
          className="py-12"
          size={56}
          label="Creating your top-up request..."
        />
      )}

      {/* Success Step */}
      {currentStep === 'success' && (
        <div className="space-y-6">
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: 'clamp(1.125rem, 2.5vw, 1.25rem)',
              fontWeight: 'var(--font-weight-bold)',
              color: '#1f2937',
              textAlign: 'center',
              marginBottom: '8px'
            }}>
              Top-up Request Created!
            </h2>
            <p style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: 'var(--mobile-font-small)',
              color: '#6b7280',
              textAlign: 'center'
            }}>
              Show this PIN at any EasyPay store to complete your top‑up
            </p>
          </div>

          {/* EasyPay PIN Display */}
          <Card className="bg-gradient-to-r from-[#86BE41]/10 to-[#2D8CCA]/10 border-[#86BE41]/30">
            <CardHeader>
              <CardTitle style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: 'var(--mobile-font-base)',
                fontWeight: 'var(--font-weight-bold)',
                color: '#1f2937',
                textAlign: 'center'
              }}>
                Your EasyPay PIN
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className="bg-white rounded-lg text-center mb-4"
                style={{ containerType: 'inline-size', width: '100%', padding: '16px 12px' }}
              >
                <p style={{
                  fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                  fontSize: 'clamp(12px, 4.6cqi, 26px)',
                  fontWeight: 'var(--font-weight-bold)',
                  color: '#1f2937',
                  letterSpacing: '0.02em',
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  margin: 0,
                  width: '100%'
                }}>
                  {formatPinForDisplay(easyPayPIN)}
                </p>
              </div>
              
              <Button
                onClick={handleCopyPIN}
                variant="outline"
                className="w-full"
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: 'var(--mobile-font-small)',
                  fontWeight: 'var(--font-weight-medium)'
                }}
              >
                <Copy className="w-4 h-4 mr-2" />
                {copiedPIN ? 'Copied!' : 'Copy PIN'}
              </Button>
            </CardContent>
          </Card>

          {/* Instructions */}
          <Alert className="bg-blue-50 border-blue-200">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: 'var(--mobile-font-small)',
              color: '#1e40af'
            }}>
              <strong>Next Steps:</strong>
              <ol className="mt-2 ml-4 list-decimal space-y-1">
                <li>Visit any EasyPay store</li>
                <li>Show this 14-digit PIN</li>
                <li>Pay R{issuedQuote ? formatRand(issuedQuote.tender) : ''}</li>
                <li>Your wallet is credited R{issuedQuote ? formatRand(issuedQuote.walletCredit) : ''}</li>
              </ol>
            </AlertDescription>
          </Alert>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <Button
              onClick={() => navigate('/vouchers')}
              variant="outline"
              className="flex-1"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: 'var(--mobile-font-small)',
                fontWeight: 'var(--font-weight-medium)'
              }}
            >
              View in Vouchers
            </Button>
            <Button
              onClick={handleReset}
              className="flex-1 bg-gradient-to-r from-[#86BE41] to-[#2D8CCA] text-white border-none"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: 'var(--mobile-font-small)',
                fontWeight: 'var(--font-weight-medium)'
              }}
            >
              Create Another
            </Button>
          </div>
        </div>
      )}

      {/* Error Step */}
      {currentStep === 'error' && (
        <div className="space-y-6">
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-12 h-12 text-red-600" />
            </div>
            <h2 style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: 'clamp(1.125rem, 2.5vw, 1.25rem)',
              fontWeight: 'var(--font-weight-bold)',
              color: '#1f2937',
              textAlign: 'center',
              marginBottom: '8px'
            }}>
              Request Failed
            </h2>
            <p style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: 'var(--mobile-font-small)',
              color: '#6b7280',
              textAlign: 'center'
            }}>
              {errorMessage || 'We couldn\'t create your top-up request. Please try again.'}
            </p>
          </div>

          <Button
            onClick={handleReset}
            className="w-full bg-gradient-to-r from-[#86BE41] to-[#2D8CCA] text-white border-none"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: 'var(--mobile-font-base)',
              fontWeight: 'var(--font-weight-medium)',
              padding: '16px',
              minHeight: 'var(--mobile-touch-target)'
            }}
          >
            Try Again
          </Button>
        </div>
      )}
    </div>
  );
}